import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { MapTrifold, Stack, DotsNine, Fire, ArrowsClockwise, Mountains, Thermometer, ThermometerSimple, Plant, ArrowsDownUp, ChartLineUp, SquaresFour } from "@phosphor-icons/react";
import { getConfig, getHeatPoints, getComparison, getRsStatus, getMunicipio } from "@/lib/api";
import HeatMap from "@/components/HeatMap";
import { ThermalLegend, DeltaLegend } from "@/components/Legend";
import StatsPanel from "@/components/StatsPanel";
import CompareStats from "@/components/CompareStats";
import MethodologyDialog from "@/components/MethodologyDialog";
import ChangeViewEE from "@/components/rs/ChangeViewEE";
import CorrelationView from "@/components/rs/CorrelationView";
import ThermalView from "@/components/rs/ThermalView";
import AirTempView from "@/components/rs/AirTempView";
import LandCoverView from "@/components/rs/LandCoverView";

const SECTIONS = [
  { id: "termico", label: "Térmico", icon: <Thermometer size={14} /> },
  { id: "ndvi", label: "Vegetación", icon: <Plant size={14} /> },
  { id: "cambios", label: "Cambios", icon: <ArrowsDownUp size={14} /> },
  { id: "suelo", label: "Uso del suelo", icon: <SquaresFour size={14} /> },
  { id: "aire", label: "Temp. del aire", icon: <ThermometerSimple size={14} /> },
  { id: "correlacion", label: "Correlación", icon: <ChartLineUp size={14} /> },
];

const YEAR = (y) => ({ start: `${y}-01-01`, end: `${y}-12-31` });

function DateField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="label-caps">{label}</span>
      <input
        type="date"
        value={value}
        min="2001-01-01"
        max="2024-12-31"
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full bg-[#f8faf9] border border-[#e5e7eb] rounded-md px-2.5 py-2 text-sm mono text-[#1f2937] focus:border-[#4a9b3c] outline-none transition-colors [color-scheme:dark]"
      />
    </label>
  );
}

function Preset({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-[11px] mono px-2.5 py-1 rounded border border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed] hover:text-[#1a2318] hover:-translate-y-[1px] transition-transform transition-colors"
    >
      {label}
    </button>
  );
}

function Toggle({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors ${
        active
          ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624]"
          : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

export default function Geoportal() {
  const [config, setConfig] = useState(null);
  const [view, setView] = useState("single");
  const [section, setSection] = useState("termico");
  const [rsStatus, setRsStatus] = useState(null);
  const [muni, setMuni] = useState(null);

  const [sStart, setSStart] = useState("2024-01-01");
  const [sEnd, setSEnd] = useState("2024-12-31");
  const [layer, setLayer] = useState("heat");
  const [threshold, setThreshold] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [aStart, setAStart] = useState("2014-01-01");
  const [aEnd, setAEnd] = useState("2014-12-31");
  const [bStart, setBStart] = useState("2024-01-01");
  const [bEnd, setBEnd] = useState("2024-12-31");
  const [cmpLayer, setCmpLayer] = useState("ab");
  const [cmp, setCmp] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);

  const loadSingle = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const d = await getHeatPoints(start, end);
      setData(d);
      setThreshold(d.stats.min);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompare = useCallback(async () => {
    setCmpLoading(true);
    try {
      const d = await getComparison(aStart, aEnd, bStart, bEnd);
      setCmp(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al comparar períodos");
    } finally {
      setCmpLoading(false);
    }
  }, [aStart, aEnd, bStart, bEnd]);

  useEffect(() => {
    getConfig().then(setConfig).catch(() => toast.error("No se pudo cargar la configuración"));
    getMunicipio().then(setMuni).catch(() => {});
    loadSingle("2024-01-01", "2024-12-31");
  }, [loadSingle]);

  useEffect(() => {
    let stop = false;
    const poll = () => {
      getRsStatus().then((s) => {
        if (stop) return;
        setRsStatus(s);
        if (s.state !== "done") setTimeout(poll, 15000);
      }).catch(() => {});
    };
    poll();
    return () => { stop = true; };
  }, []);

  const min = data?.stats.min ?? 0;
  const max = data?.stats.max ?? 1;
  const bbox = config?.bbox;

  const filteredPoints = useMemo(() => {
    if (!data) return [];
    return data.points.filter((p) => p.temp >= threshold);
  }, [data, threshold]);

  const absMax = useMemo(() => {
    if (!cmp) return 1;
    return Math.max(1, ...cmp.diff_points.map((p) => Math.abs(p.delta)));
  }, [cmp]);

  return (
    <div className="h-screen w-screen flex flex-col app-bg text-[#1a2318]">
      <div className="brand-topbar shrink-0" />
      {/* Header */}
      <header className="h-14 shrink-0 header-brand flex items-center justify-between px-3 sm:px-4 gap-2 z-[500]">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 lg:flex-initial">
          <div className="flex items-center h-11 px-2.5 rounded-md bg-white border border-[#e5e7eb] shadow-sm shrink-0" data-testid="unphu-logo" title="Escuela de Geomática · UNPHU">
            <img src="/logo-geomatica.jpg" alt="UNPHU · Ciencias y Tecnología · Geomática" className="h-9 object-contain" />
          </div>
          <div className="min-w-0">
            <h1 className="plex text-xs sm:text-sm lg:text-base font-bold tracking-tight leading-tight text-[#111827] truncate lg:whitespace-normal">
              Geoportal de Variabilidad Térmica y Cambios Agrícolas de Constanza
            </h1>
            <div className="text-[11px] text-[#475569] mono mt-0.5 hidden sm:block">
              Constanza · La Vega · República Dominicana
            </div>
          </div>
        </div>

        <nav className="hidden lg:flex items-center gap-1 p-1 rounded-lg border border-[#e5e7eb] bg-[#f8faf9]" data-testid="section-nav">
          {SECTIONS.map((s) => {
            const on = section === s.id;
            const badge = s.id !== "termico" && rsStatus && rsStatus.state !== "done";
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                data-testid={`section-${s.id}`}
                className={`relative text-xs px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 font-semibold ${
                  on ? "bg-[#eef7ed] text-[#1e5416]" : "text-[#334155] hover:text-[#111827] hover:bg-[#eef7ed]"
                }`}
              >
                {s.icon}{s.label}
                {badge && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" title="Cargando datos satelitales" />}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="mobile-section-nav"
            className="lg:hidden bg-[#f8faf9] border border-[#e5e7eb] rounded-md pl-2 pr-6 py-1.5 text-xs font-semibold text-[#1a2318] outline-none max-w-[38vw] truncate [color-scheme:light]">
            {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {false && (
            <div className="flex items-center gap-1 p-1 rounded-lg border border-[#e5e7eb] bg-[#f8faf9]">
              <button
                onClick={() => setView("single")}
                data-testid="tab-single"
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  view === "single" ? "bg-[#eef7ed] text-[#1a2318]" : "text-[#475569] hover:text-[#1f2937]"
                }`}
              >
                Individual
              </button>
              <button
                onClick={() => { setView("compare"); if (!cmp) loadCompare(); }}
                data-testid="tab-compare"
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  view === "compare" ? "bg-[#eef7ed] text-[#1a2318]" : "text-[#475569] hover:text-[#1f2937]"
                }`}
              >
                Comparación
              </button>
            </div>
          )}
          <MethodologyDialog />
        </div>
      </header>

      {/* Body */}
      {false && (
      <div className="flex-1 flex min-h-0">
        {/* Left sidebar */}
        <aside className="w-[300px] shrink-0 border-r border-[#e5e7eb] bg-[#ffffff] overflow-y-auto p-4 space-y-6" data-testid="left-sidebar">
          {view === "single" ? (
            <>
              <section>
                <div className="plex text-sm font-medium mb-3">Filtros</div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Preset label="2014" testId="preset-2014" onClick={() => { const y = YEAR(2014); setSStart(y.start); setSEnd(y.end); loadSingle(y.start, y.end); }} />
                  <Preset label="2019" testId="preset-2019" onClick={() => { const y = YEAR(2019); setSStart(y.start); setSEnd(y.end); loadSingle(y.start, y.end); }} />
                  <Preset label="2024" testId="preset-2024" onClick={() => { const y = YEAR(2024); setSStart(y.start); setSEnd(y.end); loadSingle(y.start, y.end); }} />
                </div>
                <div className="space-y-3">
                  <DateField label="Fecha inicio" value={sStart} onChange={setSStart} testId="single-start" />
                  <DateField label="Fecha fin" value={sEnd} onChange={setSEnd} testId="single-end" />
                  <button
                    onClick={() => loadSingle(sStart, sEnd)}
                    data-testid="apply-single"
                    className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white font-medium transition-colors"
                  >
                    <ArrowsClockwise size={15} weight="bold" /> Actualizar datos
                  </button>
                </div>
              </section>

              <section>
                <div className="label-caps mb-2">Capa de visualización</div>
                <div className="flex gap-2">
                  <Toggle active={layer === "heat"} onClick={() => setLayer("heat")} icon={<Stack size={14} />} label="Calor" testId="layer-heat" />
                  <Toggle active={layer === "points"} onClick={() => setLayer("points")} icon={<DotsNine size={14} />} label="Puntos" testId="layer-points" />
                </div>
              </section>

              {data && (
                <section>
                  <div className="label-caps mb-2 flex items-center justify-between">
                    <span>Intensidad mínima</span>
                    <span className="mono text-[#3f8e33]">{threshold.toFixed(1)}°C</span>
                  </div>
                  <Slider
                    value={[threshold]}
                    min={min}
                    max={max}
                    step={0.1}
                    onValueChange={([v]) => setThreshold(v)}
                    data-testid="intensity-slider"
                  />
                  <div className="flex justify-between mono text-[10px] text-[#475569] mt-1.5">
                    <span>{min.toFixed(1)}°C</span>
                    <span>{filteredPoints.length} / {data.points.length} pts</span>
                    <span>{max.toFixed(1)}°C</span>
                  </div>
                </section>
              )}

              {config && (
                <section className="pt-2 border-t border-[#e5e7eb]">
                  <div className="label-caps mb-2">Fuente</div>
                  <p className="text-[11px] text-[#475569] leading-relaxed">{config.source}</p>
                </section>
              )}
            </>
          ) : (
            <>
              <section>
                <div className="plex text-sm font-medium mb-3">Período A</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Preset label="2014" testId="a-preset-2014" onClick={() => { const y = YEAR(2014); setAStart(y.start); setAEnd(y.end); }} />
                  <Preset label="2019" testId="a-preset-2019" onClick={() => { const y = YEAR(2019); setAStart(y.start); setAEnd(y.end); }} />
                </div>
                <div className="space-y-2">
                  <DateField label="Inicio A" value={aStart} onChange={setAStart} testId="a-start" />
                  <DateField label="Fin A" value={aEnd} onChange={setAEnd} testId="a-end" />
                </div>
              </section>
              <section>
                <div className="plex text-sm font-medium mb-3 text-[#3f8e33]">Período B</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Preset label="2019" testId="b-preset-2019" onClick={() => { const y = YEAR(2019); setBStart(y.start); setBEnd(y.end); }} />
                  <Preset label="2024" testId="b-preset-2024" onClick={() => { const y = YEAR(2024); setBStart(y.start); setBEnd(y.end); }} />
                </div>
                <div className="space-y-2">
                  <DateField label="Inicio B" value={bStart} onChange={setBStart} testId="b-start" />
                  <DateField label="Fin B" value={bEnd} onChange={setBEnd} testId="b-end" />
                </div>
              </section>
              <button
                onClick={loadCompare}
                data-testid="apply-compare"
                className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white font-medium transition-colors"
              >
                <ArrowsClockwise size={15} weight="bold" /> Comparar períodos
              </button>
              <section>
                <div className="label-caps mb-2">Capa</div>
                <div className="flex gap-2">
                  <Toggle active={cmpLayer === "ab"} onClick={() => setCmpLayer("ab")} icon={<MapTrifold size={14} />} label="A vs B" testId="cmp-ab" />
                  <Toggle active={cmpLayer === "delta"} onClick={() => setCmpLayer("delta")} icon={<Fire size={14} />} label="Diferencia" testId="cmp-delta" />
                </div>
              </section>
            </>
          )}
        </aside>

        {/* Center map */}
        <main className="flex-1 relative min-w-0" data-testid="map-area">
          {view === "single" ? (
            <>
              {loading && <LoadingOverlay />}
              {data && (
                <HeatMap
                  points={filteredPoints}
                  min={min}
                  max={max}
                  bbox={bbox}
                  mode={layer}
                  boundaryGeoJSON={muni?.geojson}
                  testId="single-map"
                />
              )}
              <div className="absolute bottom-5 left-5 z-[500]">
                <ThermalLegend min={min} max={max} />
              </div>
              <div className="absolute top-4 left-4 z-[500] glass rounded-md px-3 py-1.5 text-[11px] mono text-[#1f2937]">
                {sStart} → {sEnd}
              </div>
            </>
          ) : cmpLayer === "ab" ? (
            <div className="h-full w-full flex">
              <div className="flex-1 relative border-r border-[#e5e7eb]">
                {cmpLoading && <LoadingOverlay />}
                {cmp && <HeatMap points={cmp.period_a.points} min={cmp.period_a.stats.min} max={cmp.period_a.stats.max} bbox={bbox} mode="heat" boundaryGeoJSON={muni?.geojson} testId="map-a" />}
                <div className="absolute top-4 left-4 z-[500] glass rounded-md px-3 py-1.5 text-[11px] mono">
                  <span className="text-[#334155]">A · </span>{aStart.slice(0, 4)}
                </div>
              </div>
              <div className="flex-1 relative">
                {cmpLoading && <LoadingOverlay />}
                {cmp && <HeatMap points={cmp.period_b.points} min={cmp.period_b.stats.min} max={cmp.period_b.stats.max} bbox={bbox} mode="heat" boundaryGeoJSON={muni?.geojson} testId="map-b" />}
                <div className="absolute top-4 left-4 z-[500] glass rounded-md px-3 py-1.5 text-[11px] mono">
                  <span className="text-[#3f8e33]">B · </span>{bStart.slice(0, 4)}
                </div>
              </div>
            </div>
          ) : (
            <>
              {cmpLoading && <LoadingOverlay />}
              {cmp && (
                <HeatMap points={cmp.diff_points} bbox={bbox} mode="points" isDelta absMax={absMax} boundaryGeoJSON={muni?.geojson} testId="map-delta" />
              )}
              <div className="absolute bottom-5 left-5 z-[500]">
                <DeltaLegend absMax={absMax} />
              </div>
            </>
          )}
        </main>

        {/* Right sidebar */}
        <aside className="w-[340px] shrink-0 border-l border-[#e5e7eb] bg-[#ffffff] overflow-y-auto p-4" data-testid="right-sidebar">
          <div className="plex text-sm font-medium mb-4">
            {view === "single" ? "Estadísticas" : "Comparación"}
          </div>
          {view === "single"
            ? (loading ? <SkeletonStats /> : <StatsPanel stats={data?.stats} min={min} max={max} />)
            : (cmpLoading ? <SkeletonStats /> : <CompareStats data={cmp} />)}
        </aside>
      </div>
      )}

      {section === "termico" && <ThermalView />}
      {section === "ndvi" && (
        <ThermalView index="ndvi" unit="" title="Vegetación (NDVI)" tid="ndvi"
          subtitle="NDVI real (Landsat 30 m) recortado a todo el municipio de Constanza."
          landsatRes="30 m" />
      )}
      {section === "cambios" && <ChangeViewEE />}
      {section === "suelo" && <LandCoverView />}
      {section === "aire" && <AirTempView />}
      {section === "correlacion" && <CorrelationView />}

      <footer className="h-8 shrink-0 border-t border-[#e5e7eb] bg-[#ffffff] flex items-center justify-between px-4 text-[10px] text-[#475569] z-[500]" data-testid="app-footer">
        <span className="truncate">© {new Date().getFullYear()} GeoConstanza · Producto de trabajo de grado · Escuela de Geomática, Facultad de Ciencias y Tecnología · UNPHU</span>
        <span className="hidden sm:inline whitespace-nowrap ml-3">Autores: Argelis Génere Benítez · Luis Miguel Zacarías Peralta</span>
      </footer>
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div className="absolute inset-0 z-[600] flex items-center justify-center bg-[#f8faf9]/70 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <ArrowsClockwise size={26} className="text-[#3f8e33] animate-spin" />
        <span className="text-xs mono text-[#334155]">Consultando NASA POWER…</span>
      </div>
    </div>
  );
}

function SkeletonStats() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-16" />)}
      </div>
      <div className="skeleton h-[150px]" />
      <div className="skeleton h-[160px]" />
    </div>
  );
}
